import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Gift, Sparkles } from 'lucide-react';
import { useMotionValue, useTransform, animate, useInView } from 'framer-motion';
import { BonusCard } from './components/BonusCard';
import { Reviews } from './components/Reviews';
import { FAQ } from './components/FAQ';
import { WhatYouReceive } from './components/WhatYouReceive';
import { ReikiBackground } from './components/ReikiBackground';
import { GiftBackground } from './components/GiftBackground';
import { InfraestructuraSistemica } from './components/InfraestructuraSistemica';
import { DiferencialHibrido } from './components/DiferencialHibrido';
import { Hero } from './components/Hero';
import { PurchaseCTA } from './components/PurchaseCTA';
import { PainPoints } from './components/PainPoints';
import { SystemicNetwork } from './components/SystemicNetwork';
import { UpsellPurchase } from './components/UpsellPurchase';
import { PortalOffer } from './components/PortalOffer';
function AnimatedCounter({
  from,
  to
}: {
  from: number;
  to: number;
}) {
  const count = useMotionValue(from);
  const rounded = useTransform(count, latest => {
    return Math.round(latest).toLocaleString('es-AR');
  });
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (isInView) {
      const controls = animate(count, to, {
        duration: 1.5,
        ease: 'easeOut'
      });
      return controls.stop;
    }
  }, [to, isInView]);
  return <motion.span ref={ref} data-id="element-0" data-mp="33nges1of2pye">{rounded}</motion.span>;
}
const testimonials = ['https://cdn.magicpatterns.com/uploads/m5wcH3RSYvXnvgvU2Au4x2/ChatGPT_Image_28_may_2026,_11_55_17.webp', 'https://cdn.magicpatterns.com/uploads/cga1WmjCDT5J8Xycvb6AR9/ChatGPT_Image_28_may_2026,_11_56_35.webp', 'https://cdn.magicpatterns.com/uploads/e7THeU2n1KzAsmxmTswuuQ/ChatGPT_Image_28_may_2026,_12_00_35.webp', 'https://cdn.magicpatterns.com/uploads/6hR9Tj6wJpSkqGb1CyDkGJ/ChatGPT_Image_28_may_2026,_12_04_32.webp', 'https://cdn.magicpatterns.com/uploads/dGkLPuYukHKUF2Rnm1wDsC/ChatGPT_Image_28_may_2026,_11_51_23.webp'];
export function App() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  useEffect(() => {
    // Meta Pixel Integration
    const pixelId = '1051253568074079';
    // @ts-ignore
    if (!window.fbq) {
      // @ts-ignore
      !function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        b.head.appendChild(t);
      }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    }
    // @ts-ignore
    window.fbq('init', pixelId);
    // @ts-ignore
    window.fbq('track', 'PageView');
  }, []);
  const nextTestimonial = () => {
    setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
  };
  const prevTestimonial = () => {
    setCurrentTestimonial(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  return <div className="min-h-screen bg-blue-50 text-blue-950 font-sans selection:bg-blue-200 selection:text-blue-950" data-id="element-1" data-mp="13fg8dhvjqyu0">
      <SystemicNetwork data-id="element-2" data-mp="1r5276480bc1h" />
      <ReikiBackground data-id="element-3" data-mp="1r2g2qdgexcnh" />

      {/* Hero Section */}
      <Hero data-id="element-4" data-mp="1w20zqic0vaxk" />

      {/* Sales Copy */}
      <section className="bg-[#fdfbf7] border-y border-blue-100 pt-[30px] pb-0" style={{
      paddingTop: "0px"
    }} data-id="element-5" data-mp="1w0zoacr3gmfb">
        <PainPoints data-id="element-6" data-mp="1m560tybu78ir" />
        <DiferencialHibrido data-id="element-7" data-mp="1m4mt1afwi0ig" />
      </section>

      <WhatYouReceive data-id="element-8" data-mp="1m3cvcmcymucc" />

      {/* Testimonials */}
      <section className="relative overflow-hidden bg-gradient-to-b from-blue-50 to-sky-50 pb-8 pt-6 md:pb-10 md:pt-8" data-id="element-9" data-mp="6nw6g14tinf5">
        {/* Soft blur background elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-100/80 via-transparent to-transparent pointer-events-none" data-id="element-10" data-mp="ano8duzphvbj" />
        <div className="absolute -left-40 top-40 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl pointer-events-none" data-id="element-11" data-mp="h72pa3r0zkes" />
        <div className="absolute -right-40 bottom-20 w-96 h-96 bg-blue-100 rounded-full blur-3xl shadow-[0_0_100px_rgba(30,64,175,0.1)] pointer-events-none" data-id="element-12" data-mp="c90w2pzvy4pl" />

        <div className="max-container px-4 relative z-10" data-id="element-13" data-mp="19tnht7j8m2jx">
          <div className="text-center mb-16" data-id="element-14" data-mp="14y83kehbi4mz">
            <h3 className="text-[#d97706] font-['Dancing_Script',_cursive] md:text-6xl text-center text-[32px]" data-id="element-15" data-mp="zxtvwxjmowph">
              Comentarios destacados ⭐
            </h3>
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} className="flex items-center justify-center gap-2 mt-4 text-blue-400 font-bold uppercase tracking-widest text-[10px]" data-id="element-16" data-mp="vvyw5xhcirdt">
              <ArrowRight className="rotate-180 w-3 h-3" data-id="element-17" data-mp="2058rlhyu6sgh" />
              <span data-id="element-18" data-mp="203nv3msyozfz">Deslizá para ver más</span>
              <ArrowRight className="w-3 h-3" data-id="element-19" data-mp="25425vcly6nhp" />
            </motion.div>
          </div>

          <div className="max-w-xl mx-auto relative" data-id="element-20" data-mp="29w3dgl867q1e">
            <div className="overflow-hidden" data-id="element-21" data-mp="29xwmu8a64od3">
              <motion.div key={currentTestimonial} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} onDragEnd={(_, info) => {
              if (info.offset.x > 100) {
                prevTestimonial();
              } else if (info.offset.x < -100) {
                nextTestimonial();
              }
            }} initial={{
              opacity: 0,
              x: 100
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -100
            }} transition={{
              duration: 0.5,
              ease: 'easeOut'
            }} className="bg-white rounded-[2rem] shadow-[0_30px_60px_-12px_rgba(30,64,175,0.15)] border border-blue-100 relative backdrop-blur-sm cursor-grab active:cursor-grabbing overflow-hidden" data-id="element-22" data-mp="2evpp4q8coy83">
                <img src={testimonials[currentTestimonial]} alt={`Testimonio ${currentTestimonial + 1}`} className="w-full h-auto object-cover" data-id="element-23" data-mp="2frv9ez1a9wxz" />
              </motion.div>
            </div>

            {/* Navigation Arrows */}
            <button onClick={prevTestimonial} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-16 bg-white p-4 rounded-full shadow-xl text-blue-600 hover:bg-blue-50 transition-colors z-20 hidden md:block" data-id="element-24" data-mp="33gehbdgf87pj">
              <ArrowRight className="rotate-180" size={24} data-id="element-25" data-mp="3n2pu1oqi414q" />
            </button>
            <button onClick={nextTestimonial} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-16 bg-white p-4 rounded-full shadow-xl text-blue-600 hover:bg-blue-50 transition-colors z-20 hidden md:block" data-id="element-26" data-mp="3n0dchconnss7">
              <ArrowRight size={24} data-id="element-27" data-mp="2zbu2mspyj7y0" />
            </button>

            {/* Pagination Dots */}
            <div className="mt-6 flex justify-center gap-3" data-id="element-28" data-mp="345evk6cp1bqc">
              {testimonials.map((_, i) => <button key={i} onClick={() => setCurrentTestimonial(i)} className={`h-3 rounded-full transition-all duration-300 ${currentTestimonial === i ? 'w-12 bg-blue-600' : 'w-3 bg-blue-200 hover:bg-blue-300'}`} data-id="element-29" data-mp="2pgsbxsg19xtb" data-mpi={i} />)}
            </div>
          </div>
          <div className="mt-5 text-center" data-id="element-30" data-mp="30v3vmqe1h010">
            <PurchaseCTA label="Quiero mi sistema completo" data-id="element-31" data-mp="30uvg0qqj7gm4" />
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="relative overflow-hidden bg-[#fdfbf7] py-12 md:py-16" data-id="element-32" data-mp="2vug505tq2l7b">
        <GiftBackground data-id="element-33" data-mp="2qvczkk9tycyt" />

        <div className="max-container px-4 relative z-10" data-id="element-34" data-mp="2ly4o12j6t76j">
          <div className="mb-8 text-center md:mb-10" data-id="element-35" data-mp="2lxl3rf62e6pc">
            <motion.div initial={{
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.4
          }} transition={{
            duration: 0.3,
            ease: [0.23, 1, 0.32, 1]
          }} className="mb-4 inline-flex items-center justify-center" data-id="element-36" data-mp="2hx1aimom3n8h">
              <div className="rounded-2xl bg-[#2e1065] p-4 text-white shadow-xl" data-id="element-37" data-mp="238y1t5j6pxfz">
                <Gift size={40} data-id="element-38" data-mp="1y8s71ghmbuto" />
              </div>
            </motion.div>

            <h2 className="font-['Playfair_Display',_serif] text-4xl font-bold text-[#2e1065] md:text-7xl" data-id="element-39" data-mp="1u76z89jxy6qc">
              Regalos <span className="text-[#6b4fa0]" data-id="element-40" data-mp="9po0cvmlerzm">exclusivos</span>
            </h2>
            <div className="mt-4 flex items-center justify-center gap-2" data-id="element-41" data-mp="dq7xw0b2vxma">
              <Sparkles className="text-[#b78b31]" size={18} data-id="element-42" data-mp="dscad6n1m4y6" />
              <p className="text-sm font-black uppercase tracking-[0.2em] text-[#765e37]" data-id="element-43" data-mp="iqvwb71mzgqj">
                100% GRATIS SOLO POR HOY
              </p>
              <Sparkles className="text-[#b78b31]" size={18} data-id="element-44" data-mp="3m6b6q9dxfsc7" />
            </div>
          </div>

          <div className="mx-auto grid max-w-4xl grid-cols-2 gap-3 md:gap-6" data-id="element-45" data-mp="3r5dzovgmu83t">
            <BonusCard number="01" title="Kit de encantamiento para la primera sesión" image="https://cdn.magicpatterns.com/uploads/pmRzXRZqmi8YYai2fYf7pQ/Diseno_sin_titulo_(1).webp" description="Cree una experiencia inolvidable desde el primer contacto con el cliente. Tarjetas, minicuestionario y guion de bienvenida." data-id="element-46" data-mp="3r3bc0i2ycr29" />
            <BonusCard number="02" title="Cartas terapéuticas: Ho'oponopono" image="https://cdn.magicpatterns.com/uploads/bzJHKSegp2KUJuhsotu3ia/Diseno_sin_titulo_(3).webp" description="Herramienta para sanar vínculos y liberar memorias de dolor a través del perdón y la gratitud." data-id="element-47" data-mp="38d20ghl9zmxw" />
            <BonusCard number="03" title="Biblioteca de Meditaciones Sistémicas" image="https://cdn.magicpatterns.com/uploads/viHe2PLREipfnY3drA8oSs/Diseno_sin_titulo_(4).webp" description="50 guiones listos de meditación para la integración emocional y el equilibrio del sistema familiar." data-id="element-48" data-mp="2u4xl9h9pc9n2" />
            <BonusCard number="04" title="Diario de integración y realineación" image="https://cdn.magicpatterns.com/uploads/wi7scaPramRe8ZCZEVAC2k/Diseno_sin_titulo_(8).webp" description="Una herramienta práctica para ayudar a los clientes a mantener un movimiento terapéutico activo entre sesiones." data-id="element-49" data-mp="26cqmkbgi7qkh" />
            <div className="col-span-2" data-id="element-50" data-mp="3vyyzage9d389">
              <BonusCard variant="horizontal" number="05" title="Protocolo de Protección Energética" image="https://cdn.magicpatterns.com/uploads/pVt6KcawPPmAL15TJPFkkm/Diseno_sin_titulo_(6).webp" description="Técnicas prácticas para limpiar, proteger y sostener la energía del terapeuta antes y después de las sesiones." data-id="element-51" data-mp="3rxx88muhvxhd" />
            </div>
          </div>

          <div className="mt-6 text-center md:mt-8" data-id="element-52" data-mp="3894os5hevqr6">
            <p className="mx-auto max-w-md font-['Playfair_Display',_serif] text-lg text-[#51405f] md:text-xl" data-id="element-53" data-mp="3482wy9ptqgz8">
              5 herramientas adicionales incluidas con tu sistema, sin costo extra.
            </p>
            <div className="mt-4" data-id="element-54" data-mp="2uaofc6s5zm5c">
              <PurchaseCTA label="Quiero mi sistema completo" data-id="element-55" data-mp="2u9n687upl9q9" />
            </div>
          </div>
        </div>
      </section>

      <UpsellPurchase data-id="element-56" data-mp="2pd5136vgv9de" />

      <section className="w-full" data-id="element-57" data-mp="2fnpqmjkoz5ub">
        <img src="https://cdn.magicpatterns.com/uploads/ixnQeuB1GGLCojQp22tMb6/hf_20260519_161050_0e03f4a6-9ed0-4f9f-895d-8019c0d38836.webp" alt="Sofía Alondra - Creadora del Sistema Profesional de Constelación Familiar Terapéutica" className="w-full h-auto" data-id="element-58" data-mp="2fmofyg7l8ld4" />
      </section>

      <Reviews data-id="element-59" data-mp="2aov8zkygnnbs" />

      <PortalOffer data-id="element-60" data-mp="2aqfwiqmcy605" />

      <FAQ data-id="element-61" data-mp="2ydxu52sazyb1" />

      <footer className="py-12 border-t border-blue-100 text-center text-blue-600 text-sm" data-id="element-62" data-mp="2ydfmzb43rzoc">
        <p data-id="element-63" data-mp="33cr2kgsqgz4c">
          © 2025 Sistema de Constelaciones Familiares. Todos los derechos
          reservados.
        </p>
      </footer>
    </div>;
}